import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  UtensilsCrossed, Store, ShoppingBag, Sparkles, Ticket, Wrench,
  ChevronDown, ChevronRight, MapPin, Heart, Bot, Stamp, Gift, Briefcase, Rocket,
} from "lucide-react";
import SmartHeader from "../../components/smart/SmartHeader";
import CustomerTabBar from "../../components/CustomerTabBar";
import { useAuth } from "../../context/AuthContext";

// SCOPRI · Fase 2 redesign — 6 macro-categorie con funzioni secondarie annidate.
// Nessuna funzione esistente viene rimossa: tutte le voci storiche sono raggruppate
// come sub-funzioni sotto la macro-categoria pertinente (progressive disclosure).
const CATEGORIES = [
  {
    id: "food",
    title: "Ordina da mangiare",
    desc: "Trattorie, pizzerie e botteghe tipiche",
    icon: UtensilsCrossed,
    color: "#00E676",
    subs: [
      { id: "food-list", t: "Ristoranti e locali", d: "Chi consegna vicino a te", to: "/lista/food", icon: UtensilsCrossed },
      { id: "near", t: "Vicino a te", d: "Cosa c'è intorno a te ora", to: "/lista/vicino", icon: MapPin },
    ],
  },
  {
    id: "activities",
    title: "Attività locali",
    desc: "Negozi di vicinato e artigiani",
    icon: Store,
    color: "#29B6F6",
    subs: [
      { id: "act-list", t: "Negozi e artigiani", d: "Le attività del territorio", to: "/lista/attivita", icon: Store },
      { id: "favorites", t: "Preferiti", d: "Le tue attività salvate", to: "/lista/preferiti", icon: Heart },
    ],
  },
  {
    id: "market",
    title: "Spesa & commissioni",
    desc: "Supermercati e consegne rapide",
    icon: ShoppingBag,
    color: "#FFA726",
    subs: [
      { id: "mercato", t: "Facciamo la spesa", d: "Ci pensiamo noi, tu rilassati", to: "/app/mercato", icon: ShoppingBag, auth: true },
    ],
  },
  {
    id: "ai_exp",
    title: "Esperienze AI",
    desc: "Ispirazioni su misura per il tuo tempo libero",
    icon: Sparkles,
    color: "#AB47BC",
    subs: [
      { id: "esperienze", t: "Serate su misura", d: "Itinerari creati dall'AI", to: "/esperienze", icon: Sparkles },
      { id: "fuma-ai", t: "Chiedi a Fuma", d: "Parla con l'assistente di quartiere", to: "/ai", icon: Bot },
      { id: "passaporto", t: "Passaporto Fuma", d: "Colleziona timbri esplorando", to: "/app/passaporto", icon: Stamp, auth: true },
    ],
  },
  {
    id: "events",
    title: "Eventi & cultura",
    desc: "Feste, sagre e concerti in zona",
    icon: Ticket,
    color: "#EC407A",
    subs: [
      { id: "eventi", t: "Sagre e concerti", d: "Cosa succede nel Canavese", to: "/lista/eventi", icon: Ticket },
      { id: "deals", t: "Offerte Fuma", d: "Sconti e promozioni attive", to: "/app/deals", icon: Gift, auth: true },
    ],
  },
  {
    id: "pros",
    title: "Professionisti",
    desc: "Servizi per la casa e la persona",
    icon: Wrench,
    color: "#FFEE58",
    subs: [
      { id: "professionisti", t: "Servizi di fiducia", d: "Idraulici, elettricisti e altro", to: "/professionisti", icon: Wrench },
      { id: "lavoro", t: "Lavoro & annunci", d: "Opportunità vicino a te", to: "/lavoro", icon: Briefcase, auth: true },
    ],
  },
];

export default function Discover() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(null);

  const go = (sub) => navigate(sub.auth && !user ? "/login" : sub.to);
  const toggle = (id) => setOpen((cur) => (cur === id ? null : id));

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white pb-36 animate-in fade-in duration-300" data-testid="discover-page">
      <SmartHeader current="scopri" />
      <main className="max-w-2xl mx-auto px-4 py-6">
        <h1 className="font-['Unbounded'] font-bold text-2xl sm:text-3xl tracking-tight leading-none">
          Scopri il <span className="text-[#00E676]">Canavese</span>
        </h1>
        <p className="text-white/50 text-sm mt-2 mb-6">Tutti i servizi di quartiere a portata di tap.</p>

        <div className="space-y-3" data-testid="discover-categories">
          {CATEGORIES.map((cat) => {
            const expanded = open === cat.id;
            return (
              <div
                key={cat.id}
                data-testid={`discover-cat-${cat.id}`}
                className="rounded-2xl bg-[#121215] border transition-colors overflow-hidden"
                style={{ borderColor: expanded ? `${cat.color}55` : "rgba(255,255,255,0.08)" }}
              >
                <button
                  data-testid={`discover-cat-${cat.id}-toggle`}
                  onClick={() => toggle(cat.id)}
                  aria-expanded={expanded}
                  className="w-full flex items-center gap-3.5 p-4 text-left active:scale-[0.99] transition-transform"
                >
                  <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0" style={{ background: `${cat.color}1f`, color: cat.color }}>
                    <cat.icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-['Unbounded'] font-bold text-sm text-white leading-tight">{cat.title}</h3>
                    <p className="text-xs text-white/45 mt-0.5 leading-snug truncate">{cat.desc}</p>
                  </div>
                  <ChevronDown className={`h-5 w-5 text-white/40 shrink-0 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} />
                </button>

                {expanded && (
                  <div className="px-3 pb-3 pt-0.5 space-y-1.5 animate-in fade-in slide-in-from-top-1 duration-200" data-testid={`discover-cat-${cat.id}-subs`}>
                    {cat.subs.map((sub) => (
                      <button
                        key={sub.id}
                        data-testid={`discover-sub-${sub.id}`}
                        onClick={() => go(sub)}
                        className="w-full flex items-center gap-3 rounded-xl bg-[#1A1A1E] hover:bg-[#20202568] border border-white/5 hover:border-white/12 p-3 text-left transition-colors group"
                      >
                        <div className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center shrink-0" style={{ color: cat.color }}>
                          <sub.icon className="h-4 w-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-sm text-white leading-tight truncate">{sub.t}</div>
                          <div className="text-[11px] text-white/45 mt-0.5 leading-snug truncate">{sub.d}</div>
                        </div>
                        <ChevronRight className="h-4 w-4 text-white/25 group-hover:text-white/60 transition-colors shrink-0" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Banner partner */}
        <div
          data-testid="discover-partner-banner"
          className="mt-6 rounded-3xl p-5 relative overflow-hidden"
          style={{ background: "linear-gradient(135deg, rgba(0,230,118,.14), rgba(0,230,118,.03))", border: "1px solid rgba(0,230,118,.28)" }}
        >
          <div className="flex items-center gap-2 text-[#00E676]">
            <Rocket className="h-5 w-5" />
            <span className="font-['Unbounded'] font-bold text-xs uppercase tracking-wide">Per le attività</span>
          </div>
          <h3 className="font-['Unbounded'] font-bold text-lg mt-2 leading-tight">Hai un'attività nel <span className="text-[#00E676]">Canavese?</span></h3>
          <p className="text-sm text-white/55 mt-1.5">Porta i tuoi clienti su Fuma e cresci nel tuo quartiere.</p>
          <button
            data-testid="discover-become-partner"
            onClick={() => navigate("/login")}
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#00E676] text-black text-sm font-semibold px-4 py-2 active:scale-95 transition-transform"
          >
            Diventa Partner Fuma <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </main>
      <CustomerTabBar active="scopri" />
    </div>
  );
}
